import { MoneyInputDirective } from '../../../../shared/ui/money-input.directive';
import { Component, OnInit, inject, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { GameService } from '../../../../core/state/game.service';
import { IconComponent } from '../../../../shared/ui/icon/icon.component';
import {
  MatchdayLoanOffer,
  ActiveMatchdayLoan,
  MatchdayLoanItem,
  AggregatedDebtResponse,
  EconomicSummaryResponse,
  LoanMovement,
} from '../../../../core/models/types';

export type LoanTabMode = 'offer' | 'active' | 'history' | 'legacy';

@Component({
  selector: 'app-matchday-loans',
  standalone: true,
  imports: [MoneyInputDirective, CommonModule, FormsModule, IconComponent],
  templateUrl: './matchday-loans.component.html',
  styleUrl: './matchday-loans.component.scss',
})
export class MatchdayLoansComponent implements OnInit {
  readonly game = inject(GameService);

  readonly activeSubTab = signal<LoanTabMode>('offer');

  // Input states for loan contracting
  requestedPrincipal = signal<number>(1000000);
  private principalEdited = false;
  readonly contractTermMatchdays = computed(() => this.offer()?.maximumTermMatchdays ?? 0);
  readonly contractMaturityMatchday = computed(() => this.offer()?.maturityMatchday ?? null);

  // Input states for loan repayment
  repayAmount = signal<number>(0);

  // UI Modals
  readonly isConfirmContractModalOpen = signal<boolean>(false);
  readonly isRepayModalOpen = signal<boolean>(false);
  readonly isMovementModalOpen = signal<boolean>(false);
  readonly selectedLoanForDetail = signal<ActiveMatchdayLoan | MatchdayLoanItem | null>(null);

  // Idempotency tracking (preserve UUID during retries of same operation)
  contractRequestId = signal<string | null>(null);
  repayRequestId = signal<string | null>(null);

  // Form submission loading
  readonly isSubmitting = signal<boolean>(false);
  readonly formError = signal<string | null>(null);

  // Pagination for loans history
  historyPage = signal<number>(0);
  readonly historyPageSize = 20;
  hasMoreHistory = signal<boolean>(true);

  // Computed shortcuts from game state
  readonly offer = this.game.matchdayLoanOffer;
  readonly activeLoan = this.game.activeMatchdayLoan;
  readonly history = this.game.matchdayLoansHistory;
  readonly debt = this.game.aggregatedDebt;
  readonly summary = this.game.economicSummary;
  readonly isLoading = this.game.isLoansLoading;
  readonly serverError = this.game.loansError;

  readonly effectiveClubId = computed<string>(() => {
    return this.game.activeUserClubId() || this.game.userClub()?.id || 'club-user';
  });

  readonly availableBalanceEur = computed<number>(() => {
    const sum = this.summary();
    if (sum?.cash?.availableBalance !== undefined) return sum.cash.availableBalance;
    if (sum?.availableBalance !== undefined) return sum.availableBalance;
    const club = this.game.userClub();
    return (club?.economy?.availableBalance ?? 100) * 1_000_000;
  });

  readonly reservedBalanceEur = computed<number>(() => {
    const sum = this.summary();
    if (sum?.cash?.reservedBalance !== undefined) return sum.cash.reservedBalance;
    if (sum?.reservedBalance !== undefined) return sum.reservedBalance;
    const club = this.game.userClub();
    return (club?.economy?.reservedBalance ?? 0) * 1_000_000;
  });

  readonly isEligible = computed<boolean>(() => {
    const off = this.offer();
    if (!off) return false;
    if (off.ineligibilityReason) return false;
    if (this.hasActiveLoan()) return false;
    return (off.maximumPrincipal ?? 0) > 0 && (off.maximumTermMatchdays ?? 0) >= 1;
  });

  readonly hasActiveLoan = computed<boolean>(() => {
    const l = this.activeLoan();
    return !!(l && l.status === 'ACTIVE' && l.outstandingPrincipal > 0);
  });

  readonly hasLegacyDebt = computed<boolean>(() => {
    const d = this.debt();
    return !!(d && (d.legacyLoansDebt > 0 || (d.nextInstallments && d.nextInstallments.length > 0)));
  });

  readonly maxRepayableAmount = computed<number>(() => {
    const outstanding = this.activeLoan()?.outstandingPrincipal ?? 0;
    const available = Math.max(0, this.availableBalanceEur());
    return Math.min(outstanding, available);
  });

  // Estimated interest for current contract form values
  readonly estimatedContractInterest = computed<number>(() => {
    const principal = this.requestedPrincipal();
    const rate = this.offer()?.ratePerMatchday ?? 0.01;
    const term = this.contractTermMatchdays();
    return Math.round(principal * rate * term * 100) / 100;
  });

  async ngOnInit(): Promise<void> {
    await this.refreshAllLoansData(true);

    // If an active loan exists, navigate to active tab by default
    if (this.hasActiveLoan()) {
      this.activeSubTab.set('active');
    }
  }

  async refreshAllLoansData(forceRefresh: boolean = true): Promise<void> {
    const clubId = this.effectiveClubId();
    if (!clubId) return;

    this.formError.set(null);

    await Promise.all([
      this.game.fetchMatchdayLoanOffer(clubId, forceRefresh),
      this.game.fetchAggregatedDebt(clubId, forceRefresh),
      this.game.fetchEconomicSummary(clubId, forceRefresh),
      this.loadHistoryPage(0),
    ]);

    // Set initial form values based on received offer
    const off = this.offer();
    if (off && !this.principalEdited) {
      if (off.maximumPrincipal > 0) {
        const halfMax = Math.round(off.maximumPrincipal / 2);
        this.requestedPrincipal.set(Math.min(2_000_000, halfMax));
      }
    }
  }

  async loadHistoryPage(page: number): Promise<void> {
    const clubId = this.effectiveClubId();
    if (!clubId) return;

    this.historyPage.set(page);
    const items = await this.game.fetchMatchdayLoansHistory(clubId, page, this.historyPageSize);
    this.hasMoreHistory.set(items.length === this.historyPageSize);
  }

  setSubTab(tab: LoanTabMode): void {
    this.activeSubTab.set(tab);
    this.formError.set(null);
  }

  // --- Percentage Helpers for Contract Principal ---
  setRequestedPrincipal(value: number | null): void {
    this.principalEdited = true;
    this.requestedPrincipal.set(value ?? 0);
  }

  setPrincipalPercentage(pct: number): void {
    const max = this.offer()?.maximumPrincipal ?? 3_000_000;
    const val = Math.round((max * pct) / 100);
    this.setRequestedPrincipal(val);
  }

  // --- Percentage Helpers for Repayment Amount ---
  setRepayPercentage(pct: number): void {
    const max = this.maxRepayableAmount();
    const val = Math.round((max * pct) / 100);
    this.repayAmount.set(val);
  }

  // --- Contract Loan Flow ---
  openContractConfirmModal(): void {
    this.formError.set(null);
    const principal = this.requestedPrincipal();
    const term = this.contractTermMatchdays();
    const off = this.offer();

    if (!off) {
      this.formError.set('No se dispone de una oferta de crédito válida.');
      return;
    }

    if (!principal || principal <= 0) {
      this.formError.set('Debes indicar un importe positivo para el capital a solicitar.');
      return;
    }

    if (principal > off.maximumPrincipal) {
      this.formError.set(
        `El capital solicitado supera el límite autorizado de ${this.formatEur(off.maximumPrincipal)}.`
      );
      return;
    }

    if (!this.isEligible() || !this.contractMaturityMatchday() || term < 1) {
      this.formError.set(
        'No hay una oferta válida con vencimiento en la penúltima jornada.'
      );
      return;
    }

    // Generate idempotency UUID if not already present
    if (!this.contractRequestId()) {
      this.contractRequestId.set(crypto.randomUUID());
    }

    this.principalEdited = true;
    this.isConfirmContractModalOpen.set(true);
  }

  closeContractConfirmModal(): void {
    this.isConfirmContractModalOpen.set(false);
  }

  async confirmAndSubmitLoanContract(): Promise<void> {
    if (this.isSubmitting()) return;

    const clubId = this.effectiveClubId();
    const principal = this.requestedPrincipal();
    const reqId = this.contractRequestId() || crypto.randomUUID();
    this.contractRequestId.set(reqId);

    this.isSubmitting.set(true);
    this.formError.set(null);

    try {
      await this.game.contractMatchdayLoan(clubId, principal, reqId);
      this.contractRequestId.set(null);
      this.isConfirmContractModalOpen.set(false);
      this.activeSubTab.set('active');
    } catch (err: any) {
      const msg =
        err?.responseBody?.detail ||
        err?.responseBody?.message ||
        err?.message ||
        'Error al formalizar la contratación del crédito.';
      this.formError.set(msg);
      // If 409 conflict, close modal and show error on screen
      if (err?.status === 409) {
        this.isConfirmContractModalOpen.set(false);
      }
    } finally {
      this.isSubmitting.set(false);
    }
  }

  // --- Repay Loan Flow ---
  openRepayModal(): void {
    this.formError.set(null);
    const max = this.maxRepayableAmount();

    if (max <= 0) {
      this.formError.set(
        'No dispones de saldo disponible para amortizar en este momento. El saldo reservado no puede gastarse.'
      );
      return;
    }

    this.repayAmount.set(max);
    if (!this.repayRequestId()) {
      this.repayRequestId.set(crypto.randomUUID());
    }
    this.isRepayModalOpen.set(true);
  }

  closeRepayModal(): void {
    this.isRepayModalOpen.set(false);
  }

  async confirmAndSubmitRepayment(): Promise<void> {
    if (this.isSubmitting()) return;

    const clubId = this.effectiveClubId();
    const loan = this.activeLoan();
    if (!loan || !loan.id) {
      this.formError.set('No hay un préstamo activo que amortizar.');
      return;
    }

    const amount = Number(this.repayAmount());
    const max = this.maxRepayableAmount();

    if (!amount || amount <= 0) {
      this.formError.set('Introduce un importe positivo para la amortización.');
      return;
    }

    if (amount > max) {
      this.formError.set(
        `El importe máximo amortizable con tu saldo disponible actual es de ${this.formatEur(max)}.`
      );
      return;
    }

    const reqId = this.repayRequestId() || crypto.randomUUID();
    this.repayRequestId.set(reqId);

    this.isSubmitting.set(true);
    this.formError.set(null);

    try {
      await this.game.repayMatchdayLoan(clubId, loan.id, amount, reqId);
      this.repayRequestId.set(null);
      this.isRepayModalOpen.set(false);
    } catch (err: any) {
      const msg =
        err?.responseBody?.detail ||
        err?.responseBody?.message ||
        err?.message ||
        'Error al procesar la amortización.';
      this.formError.set(msg);
      if (err?.status === 409) {
        this.isRepayModalOpen.set(false);
      }
    } finally {
      this.isSubmitting.set(false);
    }
  }

  // --- Movement Details Modal ---
  openMovementsModal(loan: ActiveMatchdayLoan | MatchdayLoanItem): void {
    this.selectedLoanForDetail.set(loan);
    this.isMovementModalOpen.set(true);
  }

  closeMovementsModal(): void {
    this.isMovementModalOpen.set(false);
    this.selectedLoanForDetail.set(null);
  }

  // --- Formatters & Humanizers ---
  formatEur(amount?: number | null): string {
    if (amount === undefined || amount === null || isNaN(amount)) return '0,00 €';
    return amount.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
  }

  /**
   * Formatea el tipo de interés por jornada como porcentaje oficial (ej. 0.010 -> "1 % por jornada")
   * NUNCA TAE ni tipo anual.
   */
  formatRate(rate?: number | null): string {
    if (rate === undefined || rate === null || isNaN(rate)) return '0 % por jornada';
    const pct = rate * 100;
    const formatted = pct.toLocaleString('es-ES', { minimumFractionDigits: 1, maximumFractionDigits: 2 });
    return `${formatted} % por jornada`;
  }

  getTierLabel(tier?: string): string {
    if (!tier) return '';
    const t = tier.toUpperCase();
    if (t === 'TOP') return 'Tercio Superior (Clasificación Alta)';
    if (t === 'MIDDLE') return 'Tercio Medio (Clasificación Media)';
    if (t === 'BOTTOM') return 'Tercio Inferior (Clasificación Baja)';
    return `Tercio: ${tier}`;
  }

  getIneligibilityExplanation(reason?: string | null): { title: string; desc: string; icon: string } {
    if (!reason) {
      return {
        title: 'Línea de crédito no disponible',
        desc: 'Actualmente no cumples los requisitos para solicitar financiación.',
        icon: 'alert-circle',
      };
    }

    switch (reason.toUpperCase()) {
      case 'EXISTING_DEBT':
        return {
          title: 'Deuda pendiente de liquidación',
          desc: 'Tu club ya cuenta con un crédito o deuda bancaria pendiente. La normativa de la liga solo permite un crédito por jornada simultáneo. Liquida la deuda actual para desbloquear nuevas ofertas.',
          icon: 'coins',
        };
      case 'CLOSING':
        return {
          title: 'Cierre de jornada en curso',
          desc: 'Se está procesando el cierre de la jornada de competición. Las operaciones crediticias quedan temporalmente pausadas hasta la publicación de resultados oficiales.',
          icon: 'calendar',
        };
      case 'SEASON_END':
        return {
          title: 'Fin de temporada próximo',
          desc: 'No restan jornadas suficientes en la temporada oficial para cubrir el plazo mínimo de contratación permitido.',
          icon: 'trophy',
        };
      case 'NO_CALENDAR':
        return {
          title: 'Calendario oficial no disponible',
          desc: 'No se puede determinar la ventana de jornadas de competición oficial para fijar los cobros y el vencimiento.',
          icon: 'alert-circle',
        };
      default:
        return {
          title: 'Operación no permitida',
          desc: reason,
          icon: 'alert-circle',
        };
    }
  }

  getMovementLabel(type: string): { label: string; badgeClass: string } {
    switch (type?.toUpperCase()) {
      case 'DISBURSEMENT':
        return {
          label: 'Concesión',
          badgeClass: 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/60 dark:text-emerald-400 dark:border-emerald-800',
        };
      case 'INTEREST':
        return {
          label: 'Intereses',
          badgeClass: 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/60 dark:text-amber-400 dark:border-amber-800',
        };
      case 'VOLUNTARY_REPAYMENT':
        return {
          label: 'Amortización',
          badgeClass: 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950/60 dark:text-blue-400 dark:border-blue-800',
        };
      case 'MATURITY_REPAYMENT':
        return {
          label: 'Liquidación',
          badgeClass: 'bg-purple-50 text-purple-700 border-purple-200 dark:bg-purple-950/60 dark:text-purple-400 dark:border-purple-800',
        };
      default:
        return {
          label: type || 'Movimiento',
          badgeClass: 'bg-slate-100 text-slate-700 border-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700',
        };
    }
  }
}
